/**
 * Compass Calibration UI Component
 *
 * Shows when sensor calibration quality is poor.
 * Guides user through figure-8 motion to improve magnetic sensor accuracy.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';

const clampScore = (score) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round(score)));
};

const scoreTone = (score) => {
  if (score == null) {
    return 'bg-slate-400';
  }
  if (score >= 80) {
    return 'bg-emerald-400';
  }
  if (score >= 50) {
    return 'bg-amber-400';
  }
  return 'bg-rose-400';
};

// Animated figure-8 path component
const Figure8Animation = () => {
  const svgVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const pathVariants = {
    hidden: { pathLength: 0 },
    visible: { 
      pathLength: 1,
      transition: { duration: 2, ease: 'easeInOut', repeat: Infinity },
    },
  };

  return (
    <div className="flex justify-center my-4">
      <svg
        width="200"
        height="160"
        viewBox="0 0 200 160"
        className="drop-shadow-lg"
        initial="hidden"
        animate="visible"
        variants={svgVariants}
      >
        {/* Instruction text */}
        <text x="100" y="20" textAnchor="middle" className="text-xs fill-amber-300" fontSize="10">
          Dessinez un 8
        </text>

        {/* Figure-8 path with animated stroke */}
        <motion.path
          d="M 100,50 Q 130,70 100,90 Q 70,110 100,130 Q 130,150 100,170 Q 70,190 100,210"
          stroke="#fbbf24"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={pathVariants}
          style={{ filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))' }}
        />

        {/* Animated phone indicator */}
        <motion.circle
          cx="100"
          cy="80"
          r="8"
          fill="#fbbf24"
          animate={{ 
            x: [0, 20, -20, 0],
            y: [0, 30, 30, 60],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          opacity="0.7"
        />
      </svg>
    </div>
  );
};

const CalibrationUI = ({
  needsCalibration,
  status, // 'initializing' | 'good' | 'noisy' | 'stuck'
  variance,
  nativeAccuracyLevel,
  nativeAccuracy,
  nativeAccuracyUnit,
  accuracyScore,
  onDismiss,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [hadCalibrationWarning, setHadCalibrationWarning] = useState(false);
  const normalizedScore = clampScore(accuracyScore);

  useEffect(() => {
    if (needsCalibration && status !== 'initializing') {
      setHadCalibrationWarning(true);
      setShowSuccess(false);
      return undefined;
    }

    if (!needsCalibration && status === 'good' && hadCalibrationWarning) {
      setShowSuccess(true);
      const timeout = setTimeout(() => {
        setShowSuccess(false);
        setHadCalibrationWarning(false);
      }, 3000);

      return () => clearTimeout(timeout);
    }

    if (status === 'initializing') {
      setShowSuccess(false);
    }

    return undefined;
  }, [hadCalibrationWarning, needsCalibration, status]);

  if (status === 'initializing') {
    return (
      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm text-blue-200/90 flex items-start gap-2">
        <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse mt-0.5 shrink-0" />
        <div>Initialisation du compas... Bougez le téléphone pour améliorer la précision</div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-100 flex items-start gap-2">
        <div className="w-4 h-4 rounded-full bg-emerald-400 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Calibration suffisante</div>
          <div className="text-emerald-100/80 text-xs mt-1">
            Le compas est redevenu stable. Vous pouvez maintenant vous fier a l&apos;indication de la Qibla.
          </div>
        </div>
      </div>
    );
  }

  if (!needsCalibration || status === 'good') {
    return null;
  }

  return (
    <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
            {status === 'stuck' ? '⚠️ Capteur magnétique gelé' : '⚠️ Compas instable'}
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-200/70 mt-1">
            {status === 'stuck'
              ? 'Le capteur magnétique est bloqué. Bougez le téléphone rapidement en tous sens ou redémarrez l\'app.'
              : 'Le compas détecte trop de bruit. Calibrez maintenant pour améliorer la précision de la Qibla.'}
          </p>
          {variance != null && (
            <p className="text-[10px] text-amber-600 dark:text-amber-200/50 mt-2 font-mono">
              Variance: {variance.toFixed(2)} (seuil: 15)
            </p>
          )}
          {nativeAccuracyLevel && nativeAccuracyLevel !== 'unknown' && (
            <p className="text-[10px] text-amber-600 dark:text-amber-200/60 mt-1 font-mono">
              Accuracy native: {nativeAccuracyLevel}
              {nativeAccuracy != null ? ` (${typeof nativeAccuracy === 'number' ? nativeAccuracy.toFixed(1) : nativeAccuracy} ${nativeAccuracyUnit || ''})` : ''}
            </p>
          )}
        </div>
      </div>

      {normalizedScore != null && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="font-semibold text-amber-800 dark:text-amber-200/90">Qualite native du compas</span>
            <span className="font-mono text-amber-700 dark:text-amber-200/80">{normalizedScore}%</span>
          </div>
          <div className="h-2 rounded-full bg-black/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${scoreTone(normalizedScore)}`}
              style={{ width: `${normalizedScore}%` }}
            />
          </div>
          <div className="text-[11px] text-amber-800 dark:text-amber-200/75">
            {normalizedScore < 40
              ? 'Continuez les mouvements en 8: la mesure native indique encore une calibration insuffisante.'
              : normalizedScore < 75
                ? 'La calibration progresse. Continuez quelques secondes pour stabiliser davantage le compas.'
                : 'La calibration est presque suffisante. Encore quelques mouvements lents pour finir.'}
          </div>
        </div>
      )}

      {/* Figure-8 Animated Guide */}
      <Figure8Animation />

      {/* Instructions */}
      <div className="bg-amber-500/5 rounded p-3 space-y-3">
        <div className="text-xs text-amber-800 dark:text-amber-200/80 font-semibold">Calibration en 4 étapes:</div>
        
        <div className="text-xs text-amber-800 dark:text-amber-200/80 space-y-2">
          <div className="flex items-start gap-2">
            <span className="font-bold text-amber-700 dark:text-amber-300 shrink-0">1.</span>
            <span>Tenez le téléphone <strong>à la hauteur du cœur</strong></span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-amber-700 dark:text-amber-300 shrink-0">2.</span>
            <span>Tracez un <strong>chiffre 8 horizontal</strong> lentement dans l'air</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-amber-700 dark:text-amber-300 shrink-0">3.</span>
            <span>Couvrez tous les côtés: <strong>haut, bas, gauche, droite</strong></span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold text-amber-700 dark:text-amber-300 shrink-0">4.</span>
            <span>Continuez <strong>15-30 secondes</strong> jusqu'à ce que le message disparaisse</span>
          </div>
        </div>

        <div className="mt-3 p-2 bg-amber-500/10 rounded text-xs text-amber-800 dark:text-amber-200/80 border border-amber-500/20">
          💡 <strong>Astuce:</strong> Le capteur magnétique se recalibrera automatiquement pendant que vous bougez. Vous verrez le message disparaître quand c'est bon!
        </div>
      </div>

      <Button
        size="sm"
        variant="outline"
        onClick={onDismiss}
        className="w-full text-xs"
      >
        <X className="w-3 h-3 mr-1" />Masquer ce message
      </Button>
    </div>
  );
};

export default CalibrationUI;
